import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { ErrorService, LayoutService } from '@core/services';
import { capitalizeFirst, indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { takeUntil } from 'rxjs/operators';
import { StiiraError } from '@core/models';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { ActionItemDetails } from '@core/models/action-item-templates/action-item-details.model';
import { ActionItemInfoPost, EditActionItemInfoDialog, EditActionItemInfoForm } from '@core/models/action-item-templates/edit-action-item-info-dialog.model';
import { ActionItemDetailsStoreService } from '@core/services/action-item-details-store.service';
import { ProceedConfirmationComponent, ProceedConfirmationComponentData } from '../proceed-confirmation/proceed-confirmation.component';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { OwnerOptions } from '@core/enums/owner-options.enum';

@Component({
    selector: 'app-edit-action-item-info',
    templateUrl: './edit-action-item-info.component.html',
    styleUrl: './edit-action-item-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditActionItemInfoComponent {
  static readonly componentName = 'EditActionItemInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<EditActionItemInfoForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      actionItemDetails: ActionItemDetails;
      options: EditActionItemInfoDialog;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private dialogRef: MatDialogRef<EditActionItemInfoComponent>,
    private service: ActionItemsTemplatesService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private store: ActionItemDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      actionItemName: this.fb.control(null, Validators.required),
      description: this.fb.control(null),
      ownerEmployerId: this.fb.control(null),
      allowUse: this.fb.control(null),
      allowUseByAllEmployers: this.fb.control(null),
      allowEmployerAssignments: this.fb.control(null)
    })
  }

  ngOnInit(): void {
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    if (!this.data.actionItemDetails.ownerEmployerId) {
      this.data.options.employers = [ { id: OwnerOptions.Stock, description: "-- " + this.data.sysText.stock + " --" } ];
    }
    this.isMobile$ = this.layoutService.isMobile$();
    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(overrideWarning: boolean): void {
    const dto: ActionItemInfoPost = {
      overrideWarning: overrideWarning,
      actionItemId: this.data.actionItemDetails.id,
      actionItemName: this.form.controls.actionItemName.value,
      description: this.form.controls.description.value,
      ownerEmployerId: this.form.controls.ownerEmployerId.value,
      allowUse: this.form.controls.allowUse.value,
      allowUseByAllEmployers: this.form.controls.allowUseByAllEmployers.value,
      allowEmployerAssignments: this.form.controls.allowEmployerAssignments.value
    }
      this.service.postActionItemInfo(dto)
        .pipe(indicate(this.isSubmitting$))
        .subscribe((res) => {
          if (res.showTemplateWarning || res.showLeaveCaseWarning) {
            this.openWarningDialog(res.showTemplateWarning, res.showLeaveCaseWarning);
          } else {
            this.store.actionItemDetails = res as ActionItemDetails;
            this.dialogRef.close();
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private openWarningDialog(showTemplateWarning: boolean, showLeaveCaseWarning: boolean ): void {
    this.dialog.open<
      ProceedConfirmationComponent,
      ProceedConfirmationComponentData>
      (ProceedConfirmationComponent, {
        id: ProceedConfirmationComponent.componentName,
        panelClass: "mat-dialog-container-mobileWidth",
        width: '450px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          title: this.data.sysText.templateWarnTitle,
          message1: showTemplateWarning ? this.data.sysText.templateWarnMsg : null,
          message2: showLeaveCaseWarning ? 
            (showTemplateWarning ? (this.data.sysText.inAddition + ' ' + this.data.sysText.leaveCaseWarnMsg) : 
            capitalizeFirst(this.data.sysText.leaveCaseWarnMsg)) : 
            null,
          cancelCaption: this.data.sysText.cancel,
          proceedCaption: this.data.sysText.proceed
        }
      }).beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.save(true);
        }
      });
  }

  private setupForm(): void {
    this.form.patchValue({
      actionItemName: this.data.actionItemDetails.name,
      description: this.data.actionItemDetails.description,
      ownerEmployerId: this.data.actionItemDetails.ownerEmployerId ?? OwnerOptions.Stock,
      allowUse: this.data.actionItemDetails.allowUse,
      allowUseByAllEmployers: this.data.actionItemDetails.allowUseByAllEmployers,
      allowEmployerAssignments: this.data.actionItemDetails.allowEmployerAssignments
    });

    if (!this.data.options.canEditNameAndDescription) {
      this.form.controls.actionItemName.disable();
      this.form.controls.description.disable();
    }

    if (!this.data.options.canEditOwner) {
      this.form.controls.ownerEmployerId.disable();
    }

    if (!this.data.options.canEditSharingOptions) {
      this.form.controls.allowUseByAllEmployers.disable();
      this.form.controls.allowEmployerAssignments.disable();
    }

    if (!this.data.options.canEditAllowUse) {
      this.form.controls.allowUse.disable();
    }

    this.formInitValues = { ...this.form.value };
    this.setFormErrors();

    this.form.controls.allowUseByAllEmployers.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
            this.form.controls.allowEmployerAssignments.setValue(false);
        });

    this.form.controls.allowEmployerAssignments.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
          this.form.controls.allowUseByAllEmployers.setValue(false);
        });
  }
}
